import {
  ComboboxLikeRenderOptionInput,
  ComboboxItem,
  Group,
  Text,
  Box,
} from "@mantine/core"
import { IconComponents, IconHanger, IconShoe } from "@tabler/icons-react"
import { InformationRequestStatus } from "@tfg/db"
import { FilterBuilderProps } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"
import { getTraceabilityRequestsQueryParams } from "@tfg/types/schemas/traceabilityRequests"
import { removeUndefined } from "@tfg/types/schemas/utils"

import { IconRawMaterial } from "~/components/icons/activities/IconRawMaterial"
import { useGetTraceabilityRequestsClients } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useGetTraceabilityRequestsClients"
import { useGetTraceabilityRequestsTypes } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useGetTraceabilityRequestsTypes"
import { useAuth } from "~/store/auth-context"
import { getTranslation } from "~/utils/translations"

interface Props {
  filters: getTraceabilityRequestsQueryParams["filters"]
}

export const useGetTraceabilityRequestsFilterOptions = ({ filters }: Props) => {
  const { t } = useTranslation(["traceability"])
  const { isAdmin } = useAuth()
  const { data: types } = useGetTraceabilityRequestsTypes({
    filters: removeUndefined({ ...filters, type: undefined }),
  })
  const { data: clients } = useGetTraceabilityRequestsClients({
    filters: removeUndefined({ clientId: undefined, ...filters }),
  })

  const options: FilterBuilderProps<
    Exclude<getTraceabilityRequestsQueryParams["filters"], undefined>
  >["options"] = useMemo(() => {
    const itemTypeOptions: {
      [key in TraceabilityResponses.TraceabilityRequestItem["type"]]: string
    } = {
      product: t("itemType.product"),
      component: t("itemType.component"),
      material: t("itemType.material"),
    }
    const getItemTypeTranslation = (
      type: TraceabilityResponses.TraceabilityRequestItem["type"]
    ): string => {
      return itemTypeOptions[type]
    }
    const renderTypeIcon = (
      itemType: TraceabilityResponses.TraceabilityRequestItem
    ) => {
      const type = itemType.type
      if (type === "product") {
        return type.toLowerCase().includes("footwear") ? (
          <IconShoe size={16} />
        ) : (
          <IconHanger size={16} />
        )
      }
      if (type === "component") return <IconComponents size={16} />
      if (type === "material") return <IconRawMaterial size={16} />
      return null
    }

    const STATUS_OPTIONS: {
      [key in InformationRequestStatus]: string
    } = {
      [InformationRequestStatus.OPENED]: t("filters.status.opened"),
      [InformationRequestStatus.CLOSED]: t("filters.status.closed"),
      [InformationRequestStatus.CANCELED]: t("filters.status.canceled"),
    }

    return [
      {
        label: t("filters.status.placeholder"),
        key: "status",
        options: Object.entries(STATUS_OPTIONS)
          .filter(
            ([key]) => isAdmin || key !== InformationRequestStatus.CANCELED
          )
          .map(([key, value]) => ({
            label: value,
            value: key,
          })),
        type: "select",
      },
      {
        label: t("filters.type"),
        key: "type",
        type: "select",
        options:
          types?.map((item) => ({
            label: `${getItemTypeTranslation(item.type)}-${item.typeTextContent ? getTranslation(item.typeTextContent) : ""}`,
            value: `${item.typeTextContent?.originalText}`,
            item,
          })) ?? [],
        customRender: ({
          option,
          checked,
        }: ComboboxLikeRenderOptionInput<
          ComboboxItem & {
            item?: TraceabilityResponses.TraceabilityRequestItem
          }
        >) => {
          const { item } = option
          if (!item) return null
          return (
            <Group
              data-checked={checked}
              py="var(--tfg-molecule-padding-y)"
              px="var(--tfg-molecule-padding-x)"
              gap="var(--tfg-molecule-padding-y)"
              wrap="nowrap"
            >
              <Box miw={16}>{renderTypeIcon(item)}</Box>
              <Text>{option.label}</Text>
            </Group>
          )
        },
      },
      {
        label: t("filters.client"),
        key: "clientId",
        options:
          clients?.map((client) => ({
            label: client.name,
            value: client.id,
          })) ?? [],
        type: "select",
      },
    ]
  }, [types, clients, isAdmin, t])
  return options
}
